import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VehicleDutyCycleFigureService } from 'src/app/_services/vehicle-duty-cycle-figure.service';
import { CelsiusToFahrenheitPipe } from 'src/app/_shared/pipes/celsius-to-fahrenheit.pipe';
import { RangeYPipe } from 'src/app/_shared/pipes/range-y.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-temperature-comfort-zone-chart',
  templateUrl: './temperature-comfort-zone-chart.component.html',
  styleUrls: ['./temperature-comfort-zone-chart.component.scss']
})
export class TemperatureComfortZoneChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles!: any[];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  private readonly celsiusToFahrenheitPipe = new CelsiusToFahrenheitPipe();
  private readonly rangeYPipe = new RangeYPipe();
  deviceSerialNumber!: any[];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  colorMap: { [key: string]: string } = {};

  constructor(
    private readonly vehicleDutyCycleFigureService:VehicleDutyCycleFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;

    this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    this.vehicleDutyCycleFigureService.getTemperatureComfortZoneUsage(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
      this.plotTemperatureComfortZoneChart(data);
      this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching Temperature Comfort Zone Usage data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    })
  }

  plotTemperatureComfortZoneChart(data: any): void {
    const plottedVehicles = new Set();
    const dataMap: any[] = [];
    const allTemperatures: any[] = [];
    this.initializeColorMap();
    Object.keys(data).flatMap((deviceSerialNumberId: string) => {
      const deviceDataArray = data[deviceSerialNumberId].timeInComfortZone.filter((item: any) => item.operationTime != 0);
      const xValues = deviceDataArray.map((dataObject: any) => this.getConvertedTemperatureValue(dataObject.batteryTemp)); // Convert to Fahrenheit
      const yValues = deviceDataArray.map((dataObject: any) => dataObject.operationTime);
      const vehicle = this.selectedVehicles.find(v => v.batteryIdentifier === deviceSerialNumberId);

      allTemperatures.push(...xValues);

      const totalOpTime = yValues.reduce((acc: any, operationTime: any) => acc + operationTime, 0);
      const normalizedYValues = yValues.map((operationTime: number) => (operationTime / totalOpTime) * 100); // Normalize operation time

      for (let i = 0; i < xValues.length - 1; i++) {
        dataMap.push({
          x: [xValues[i], xValues[i + 1]],
          y: [normalizedYValues[i], normalizedYValues[i + 1]],
          type: 'scatter',
          mode: 'lines',
          name: vehicle.name,
          line: { color: this.colorMap[vehicle.batteryIdentifier] },
          showlegend: !plottedVehicles.has(vehicle.batteryIdentifier)
        });
        plottedVehicles.add(vehicle.batteryIdentifier);
      }
    });

    const minTemp = Math.min(...allTemperatures);
    const maxTemp = Math.max(...allTemperatures);

    const allYValues = dataMap.flatMap((trace: any) => trace.y || []);
    const [yMin, yMax] = this.rangeYPipe.transform(allYValues, 5);

    if (allTemperatures.length === 0) {
      return;
    }

    const linspace = (start: number, end: number, num: number) => {
      if (num === 1) return [start];
      const step = (end - start) / (num - 1);
      return Array.from({ length: num }, (_, i) => start + i * step);
    };

    const gradientSteps = 200;
    const xGradient = linspace(minTemp, maxTemp, gradientSteps);
    const gradientRow = linspace(0, 1, gradientSteps);
    const gradientTrace = {
      x: xGradient,
      y: [yMin, yMax],
      z: [gradientRow, gradientRow],
      type: 'heatmap',
      colorscale: [
        [0, '#56B0FF'],
        [0.33, '#FFDF95'],
        [0.66, '#5EBD82'],
        [1, '#FF8787']
      ],
      showscale: false,
      hoverinfo: 'skip',
      opacity: 0.25,
      zmin: 0,
      zmax: 1
    };

    dataMap.unshift(gradientTrace);

    const layout = {
      title: 'Temperature comfort zone usage',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      xaxis: {
        title: this.getTemperatureAxisLabel(),
      },
      yaxis: {
        title: 'Operation time [%]',
        range: [yMin, yMax]
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
        itemclick: false,
        itemdoubleclick: false,
      }
    };

    createPlotlyChart('temperature-comfort-zone-chart', dataMap, layout);
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  getConvertedTemperatureValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.celsiusToFahrenheitPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.celsiusToFahrenheitPipe.transform(val)
        : val;
    }
  }

  getTemperatureAxisLabel(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Battery Temperature [°C]' : 'Battery Temperature [°F]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Battery Temperature [°F]'
        : 'Battery Temperature [°C]';
    }
  }

  getThresholdValue(celsiusValue: number, fahrenheitValue: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? celsiusValue : fahrenheitValue;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? fahrenheitValue
        : celsiusValue;
    }
  }
}
